import { CircularProgress, Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import { BundlePatientSummary } from "@/services/fhir/patientsummary/BundlePatientSummary";
import { HTTPResponseStatus } from "@/utils/enum";
import { PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY } from "@/utils/patient/fetchDependentPatientSummaryQuestionnaireEntries";
import { PatientQuestionnairesDialog } from "src-next/modules/patient-summary";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  useQuestionnaireAnalysisData,
  type QuestionnaireSendData,
} from "src-next/modules/clinical-insights/hooks/useQuestionnaireAnalysisData";
import {
  normalizePatientId,
  patientDisplayName,
} from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const texts = getInsightAnalysisTexts("questionarios");

const STATUS_TRANSLATION: Record<string, string> = {
  requested: "Não respondido",
  completed: "Respondido",
};

/**
 * `BundlePatientSummary` volta a prefixar o id com o OID do CPF.
 * O id da Task já vem como `{oid}-{cpf}`.
 */
function patientSummaryIdentifier(patientId: string): string {
  const prefix = `${oids.cpf}-`;
  return patientId.startsWith(prefix) ? patientId.slice(prefix.length) : patientId;
}

interface QuestionnaireAnswersViewer {
  reference: string;
  anchorIso?: string;
  summary?: fhir5.Bundle;
  loading: boolean;
}

export function QuestionnaireAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const organizationKey = organizationId ? `${oids.cnpj}-${organizationId}` : "";
  const owner = organizationKey;
  const practitionerFilter = practitionerId || "all";

  const getRequesterName = useCallback(
    (requesterReference: string) => {
      if (!requesterReference) return "Não informado";
      const roleId = requesterReference.startsWith("PractitionerRole/")
        ? requesterReference.slice("PractitionerRole/".length)
        : requesterReference.split("/")[1] ?? "";
      const match = practitioner?.find((entry) => {
        const id = entry.resource?.id;
        if (!id) return false;
        return roleId === id || roleId.endsWith(`-${id}`);
      });
      const name = match?.resource?.name?.[0];
      const text = name?.text?.trim();
      if (text) return text;
      const built = `${name?.given?.join(" ") || ""} ${name?.family || ""}`.trim();
      return built || "Não informado";
    },
    [practitioner],
  );

  const { sends, patients, loadPhase, patientsProgress, hasData, retry } =
    useQuestionnaireAnalysisData({
      owner,
      organizationKey,
      practitionerFilter,
      dateRange,
      getRequesterName,
      onLoadPhaseChange,
      fetchEnabled: fetchWarm ?? isActive,
      isActive,
    });

  const answersRequest = useRef(0);
  const [answersViewer, setAnswersViewer] = useState<QuestionnaireAnswersViewer | null>(null);

  const closeAnswers = useCallback(() => {
    answersRequest.current += 1;
    setAnswersViewer(null);
  }, []);

  const openAnswers = useCallback(async (send: QuestionnaireSendData) => {
    if (send.status !== "completed") return;
    const requestId = answersRequest.current + 1;
    answersRequest.current = requestId;
    const reference = `Questionnaire/${send.questionnaireId}`;
    setAnswersViewer({
      reference,
      anchorIso: send.authoredOn || undefined,
      loading: true,
    });
    try {
      const bundle = await BundlePatientSummary({
        identifier: patientSummaryIdentifier(send.patientId),
        purposeofuse: "Atendimento",
        entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
      });
      if (answersRequest.current !== requestId) return;
      if (
        !bundle ||
        (typeof bundle === "object" &&
          "status" in bundle &&
          bundle.status === HTTPResponseStatus.Forbidden)
      ) {
        toast.warn("Não foi possível abrir as respostas deste paciente.");
        setAnswersViewer(null);
        return;
      }
      setAnswersViewer({
        reference,
        anchorIso: send.authoredOn || undefined,
        summary: bundle as fhir5.Bundle,
        loading: false,
      });
    } catch (error) {
      console.error("Questionnaire answers:", error);
      if (answersRequest.current !== requestId) return;
      toast.error("Não foi possível carregar as respostas.");
      setAnswersViewer(null);
    }
  }, []);

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel,
  );

  const statusItems: InsightChartItem[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const send of sends) {
      map.set(send.status, (map.get(send.status) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([key, value]) => ({
        key,
        label: STATUS_TRANSLATION[key] || key,
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [sends]);

  const rankingItems: InsightChartItem[] = useMemo(() => {
    const map = new Map<string, { label: string; value: number }>();
    for (const send of sends) {
      const current = map.get(send.questionnaireId);
      if (current) current.value += 1;
      else map.set(send.questionnaireId, { label: send.title, value: 1 });
    }
    return Array.from(map.entries())
      .map(([key, item]) => ({ key, label: item.label, value: item.value }))
      .sort((a, b) => b.value - a.value);
  }, [sends]);

  const patientRows: InsightPatientRow[] = useMemo(() => {
    const groups = new Map<string, { name: string; items: QuestionnaireSendData[] }>();
    for (const send of sends) {
      const patientId = normalizePatientId(send.patientId);
      if (!groups.has(patientId)) {
        groups.set(patientId, {
          name: patientDisplayName(patients.get(patientId)),
          items: [],
        });
      }
      groups.get(patientId)!.items.push(send);
    }
    return Array.from(groups.entries())
      .map(([patientId, group]) => ({
        patientId,
        patientName: group.name,
        patient: patients.get(patientId),
        items: group.items.map((item) => ({
          itemKey: item.questionnaireId,
          label: item.title,
          statusKey: item.status,
          statusLabel: STATUS_TRANSLATION[item.status] || item.status,
          practitioner: item.practitioner,
          date: item.date,
          ...(item.status === "completed"
            ? { onActivate: () => void openAnswers(item) }
            : {}),
        })),
      }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [sends, patients, openAnswers]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = sends.length > 0 ? Math.round((sorted[0].value / sends.length) * 100) : 0;
    return `${percentage}% dos ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, sends.length]);

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#00838F"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={patientsProgress}
        hasData={hasData}
        isEmpty={sends.length === 0}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={retry}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={STATUS_TRANSLATION}
        onPatientStatsChange={onPatientStatsChange}
        showEvolve={false}
      />
      <Dialog
        open={Boolean(answersViewer?.loading)}
        onClose={closeAnswers}
        aria-labelledby="questionnaire-answers-loading"
      >
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1, pr: 2 }}>
            <CircularProgress size={22} />
            <Typography id="questionnaire-answers-loading">Carregando respostas…</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      {answersViewer?.summary ? (
        <PatientQuestionnairesDialog
          open
          onClose={closeAnswers}
          summaryData={answersViewer.summary}
          initialQuestionnaireReference={answersViewer.reference}
          initialQuestionnaireResponseAnchorIso={answersViewer.anchorIso}
        />
      ) : null}
    </Stack>
  );
}
